import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress
} from '@mui/material';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format } from 'date-fns';
import { accessRequestService } from '../../services/accessRequestService';
import { familyCodeService } from '../../services/familyCodeService';
import { familyMergeService } from '../../services/familyMergeService';
import { useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`access-requests-tabpanel-${index}`}
      aria-labelledby={`access-requests-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AccessRequests() {
  const { profile } = useProfile();
  const { currentUser } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let requestsData = [];

      if (profile?.familyCode) {
        // Tab 0 = vault, 1 = group, 2 = family join, 3 = merge requests
        if (currentTab === 2) {
          requestsData = await accessRequestService.getFamilyAccessRequests(profile.familyCode);
        } else if (currentTab === 3) {
          const q = query(
            collection(db, 'families', profile.familyCode, 'accessRequests'),
            where('type', '==', 'family_merge'),
            where('status', '==', 'pending')
          );
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            requestsData.push({ id: doc.id, ...doc.data() });
          });
        } else {
          const requestType = currentTab === 0 ? 'vault' : 'group';
          const q = query(
            collection(db, 'families', profile.familyCode, 'accessRequests'),
            where('type', '==', requestType),
            where('status', '==', 'pending')
          );
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            requestsData.push({ id: doc.id, ...doc.data() });
          });
        }
      }

      setRequests(requestsData);
    } catch (err) {
      setError('Failed to load access requests: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentTab, profile?.familyCode]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleRequestAction = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Use NESTED structure: /families/{familyCode}/accessRequests/{requestId}
      const familyCode = selectedRequest.familyCode || profile?.familyCode;
      if (!familyCode) {
        throw new Error('Family code not found');
      }

      const requestRef = doc(db, 'families', familyCode, 'accessRequests', selectedRequest.id);
      const userRef = doc(db, 'users', selectedRequest.userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const status = actionType === 'approve' ? 'approved' : 'rejected';

      if (actionType === 'approve') {
        if (selectedRequest.type === 'vault') {
          await updateDoc(userRef, { hasVaultAccess: true });
        } else if (selectedRequest.type === 'group') {
          // Use NESTED structure for groups: /families/{familyCode}/groups/{groupId}
          const groupRef = doc(db, 'families', familyCode, 'groups', selectedRequest.groupId);
          const groupDoc = await getDoc(groupRef);
          if (!groupDoc.exists()) {
            throw new Error('Group not found');
          }
          const members = groupDoc.data().members || [];
          if (!members.includes(selectedRequest.userId)) {
            await updateDoc(groupRef, {
              members: [...members, selectedRequest.userId]
            });
          }
        } else if (selectedRequest.type === 'family_join') {
          const assignedGroups = selectedRequest.autoAssignGroups || ['EXTENDED_FAMILY'];
          await familyCodeService.joinFamily(selectedRequest.userId, selectedRequest.familyCode, {
            autoAssignGroups: assignedGroups,
            relationship: selectedRequest.relationship || null,
            invitedBy: null
          });
        } else if (selectedRequest.type === 'family_merge') {
          await familyMergeService.executeMerge(
            selectedRequest.sourceFamilyCode,
            selectedRequest.targetFamilyCode,
            currentUser.uid,
            { defaultGroup: 'EXTENDED_FAMILY' }
          );
        }
      }

      // Update request status in NESTED structure
      await updateDoc(requestRef, { status, processedAt: new Date() });

      setSuccess(`Request ${status} successfully`);
      loadRequests();
    } catch (err) {
      setError('Failed to process request: ' + err.message);
    } finally {
      setLoading(false);
      setConfirmDialogOpen(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Vault Access Requests" />
          <Tab label="Group Access Requests" />
          <Tab label="Family Join Requests" />
          <Tab label="Merge Requests" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <TabPanel value={currentTab} index={0}>
          <Typography variant="h6" gutterBottom>
            Pending Vault Access Requests
          </Typography>
          {renderRequestsTable()}
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Pending Group Access Requests
          </Typography>
          {renderRequestsTable()}
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Pending Family Join Requests
          </Typography>
          {renderRequestsTable()}
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Pending Family Merge Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These families have requested to merge into your family. Approving will migrate all their members into your family and deactivate the source family.
          </Typography>
          {renderRequestsTable()}
        </TabPanel>
      </Paper>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>
          Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {actionType} this access request?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  function renderRequestsTable() {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (requests.length === 0) {
      return (
        <Typography variant="body1" sx={{ p: 2 }}>
          No pending requests found.
        </Typography>
      );
    }

    if (currentTab === 3) {
      return (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Source Family</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    <strong>{request.sourceFamilyName || request.sourceFamilyCode}</strong>
                    <br />
                    <small style={{ color: '#888' }}>{request.sourceFamilyCode}</small>
                  </TableCell>
                  <TableCell>
                    {request.requestedByName || request.requestedByEmail || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    {request.createdAt?.toDate
                      ? format(request.createdAt.toDate(), 'PPpp')
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <Button
                      color="primary"
                      onClick={() => handleRequestAction(request, 'approve')}
                      sx={{ mr: 1 }}
                    >
                      Approve & Merge
                    </Button>
                    <Button
                      color="error"
                      onClick={() => handleRequestAction(request, 'reject')}
                    >
                      Deny
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Requested At</TableCell>
              {currentTab === 1 && <TableCell>Group</TableCell>}
              {currentTab !== 2 && <TableCell>Reason</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.userName}</TableCell>
                <TableCell>{request.userEmail}</TableCell>
                <TableCell>
                  {request.createdAt?.toDate
                    ? format(request.createdAt.toDate(), 'PPpp')
                    : request.requestedAt?.toDate
                      ? format(request.requestedAt.toDate(), 'PPpp')
                      : 'Unknown'
                  }
                </TableCell>
                {currentTab === 1 && <TableCell>{request.groupName}</TableCell>}
                {currentTab !== 2 && <TableCell>{request.reason || 'N/A'}</TableCell>}
                <TableCell>
                  <Button
                    color="primary"
                    onClick={() => handleRequestAction(request, 'approve')}
                    sx={{ mr: 1 }}
                  >
                    Approve
                  </Button>
                  <Button
                    color="error"
                    onClick={() => handleRequestAction(request, 'reject')}
                  >
                    Reject
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
} 